<template>
	<div class="main">
		<header class="page-title">
			<h2>Large Data Tables</h2>
			<div class="ml-auto">
				<a @click="showHelpModal" class="btn btn-md btn-primary" href="#"><i class="fas fa-question-circle"></i> Help</a>
				<a @click="showNewModal" class="btn btn-md btn-success" href="#"><i class="fas fa-plus" v-show="userRoles.includes('large_data_tables.update')"></i> New Data Table</a>
			</div>
		</header>
		<div class="table-responsive">
			<table>
				<thead>
					<tr>
						<th>Name</th>
						<th>Item Count</th>
						<th>Fields</th>
						<th>Shortcode</th>
						<th>Actions</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="item in tables">
						<td>{{ item.name }}</td>
						<td>{{ item.counter }}</td>
						<td>
							<ul>
								<li v-for="column in item.columns">{{ column }}</li>
							</ul>
						</td>
						<td>
							<code class="inline-block">[[post-object type="largedatatables" set="{{ item.name }}" field="{{ item.columns[0] }}" /]]</code>
						</td>
						<td class="nowrap">
							<a @click="showEditModal(item)" class="btn btn-icon btn-primary" title="Edit" v-show="userRoles.includes('large_data_tables.update')"><i class="fas fa-pencil-alt"></i></a>
							<a @click="downloadFile(item.file)" class="btn btn-icon btn-primary" title="Download" v-show="item.file"><i class="fas fa-download"></i></a>
							<a @click="showDeleteTableModal(item)" class="btn btn-icon btn-danger" title="Delete" v-show="userRoles.includes('large_data_tables.update')"><i class="fas fa-trash"></i></a>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<modal @close="closeNewModal" v-show="isNewModalVisible">
			<template slot="header">
				<h4 class="modal-title">New Data Table</h4>
			</template>
			<template slot="body">
				<div class="form-group">
					<label for="new-name">Name</label>
					<input id="new-name" type="text" v-model="selectedTable.name"/>
					<p class="input-error" v-if="errors.errors.name">{{ errors.errors.name }}</p>
				</div>
				<div class="form-group">
					<label for="new-file">File</label>
					<input id="new-file" ref="file" type="file" v-on:change="handleFileUpload()"/>
					<p class="input-error" v-if="errors.errors.file">{{ errors.errors.file }}</p>
				</div>
			</template>
			<template slot="footer">
				<a @click="closeNewModal" aria-label="Close modal" class="btn btn-md btn-primary modal-close" href="#"><i class="fas fa-times"></i> Cancel</a>
				<a @click="submitFile" class="btn btn-md btn-success" href="#"><i class="fas fa-check"></i> Add Data Table</a>
			</template>
		</modal>

		<modal @close="closeEditModal" v-show="isEditModalVisible">
			<template slot="header">
				<h4 class="modal-title">Edit Data Table</h4>
			</template>
			<template slot="body">
				<div class="form-group">
					<label for="edit-name">Name</label>
					<input id="edit-name" type="text" v-model="selectedTable.name">
					<p class="input-error" v-if="errors.errors.name">{{ errors.errors.name }}</p>
				</div>
				<div class="form-group">
					<label for="edit-file">File</label>
					<input id="edit-file" ref="newfile" type="file" v-on:change="handleNewFileUpload()"/>
					<p class="input-error" v-if="errors.errors.file">{{ errors.errors.file }}</p>
				</div>
			</template>
			<template slot="footer">
				<a @click="closeEditModal" aria-label="Close modal" class="btn btn-md btn-primary modal-close" href="#"><i class="fas fa-times"></i> Cancel</a>
				<a @click="editName" class="btn btn-md btn-success" href="#"><i class="fas fa-save"></i> Save Changes</a>
			</template>
		</modal>

		<modal @close="closeHelpModal" v-show="isHelpModalVisible">
			<template slot="header">
				<h4 class="modal-title">Help</h4>
			</template>
			<template slot="body">
				<h4 class="mt-0">How to Use</h4>
				<div class="table-responsive">
					<table>
						<thead>
							<tr>
								<th>Parameter</th>
								<th>Description</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td><code class="inline-block">set</code> <span class="text-danger">*</span></td>
								<td>The name of the Data Table.</td>
							</tr>
							<tr>
								<td><code class="inline-block">field</code> <span class="text-danger">*</span></td>
								<td>The data table's search functionality will work based on the one column you specify here.
									<br>This value is case sensitive.
								</td>
							</tr>
							<tr>
								<td><code class="inline-block">init</code></td>
								<td>You can specify a default search value here, so that the data table will always start with this query.
									<br>You can define this parameter as
									<code class="inline-block">init="_empty_"</code>, in which case you will not see the table, only the search bar.
								</td>
							</tr>
							<tr>
								<td><code class="inline-block">perpage</code></td>
								<td>How many results do you want to see on one page. The default value is 10. <br>
									Please note that setting a high number can result in a poor user experience on smaller screens.
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<h4>Example Shortcode</h4>
				<code class="inline-block">[[post-object type="largedatatables" set="dataset_name" field="Column Name" init="query_string" perpage="25" /]]</code>
			</template>
			<template slot="footer">
				<a @click="closeHelpModal" aria-label="Close modal" class="btn btn-md btn-primary modal-close" href="#"><i class="fas fa-times"></i> Close</a>
			</template>
		</modal>
		<!-- MODAL -->
		<modal @close="closeDeleteTableModal" v-show="isDeleteTableModalVisible">
			<template slot="header">
				<h4 class="modal-title">Delete Data Table</h4>
			</template>
			<template slot="body">
				<div>
					<p>Are you sure you want to delete data table : {{ selectedTable.name }} ?</p>
				</div>
			</template>
			<template slot="footer">
				<a @click="deleteTable(selectedTable.id)" aria-label="Delete Data Table" class="btn btn-md btn-primary btn-danger" href="#"><i class="fas fa-times"></i> Delete</a>
				<a @click="closeDeleteTableModal" aria-label="Close modal" class="btn btn-md btn-primary modal-close" href="#"><i class="fas fa-times"></i> Cancel</a>
			</template>
		</modal>
		<!-- / MODAL END -->
	</div>
</template>

<script>
import modal from '../Modal/DefaultModal.vue';
import Errors from '../../Utils/Errors.js';

export default {
	props: {
		dataTables: {
			type: Object
		}
	},
	components: {
		modal
	},
	data() {
		return {
			tables: this.dataTables,

			selectedTable: {
				id: null,
				name: null
			},

			isNewModalVisible: false,
			isEditModalVisible: false,
			isHelpModalVisible: false,
			isDeleteTableModalVisible: false,

			userRoles: this.$currentUserRoles,
			errors: new Errors(),
		}
	},
	created() {
		let vm = this;
	},
	methods: {
		showNewModal: function () {
			this.isNewModalVisible = true;
		},
		closeNewModal: function () {
			this.isNewModalVisible = false;

			this.selectedTable = {
				id: null,
				name: null
			};
		},
		showEditModal: function (item) {
			this.selectedTable.id = item.id;
			this.selectedTable.name = item.name;

			this.isEditModalVisible = true;
		},
		closeEditModal: function () {
			this.isEditModalVisible = false;

			this.selectedTable = {
				id: null,
				name: null
			};
		},
		showHelpModal: function () {
			this.isHelpModalVisible = true;
		},
		closeHelpModal: function () {
			this.isHelpModalVisible = false;
		},
		showDeleteTableModal: function (item) {
			this.selectedTable.id = item.id;
			this.selectedTable.name = item.name;

			this.isDeleteTableModalVisible = true;
		},
		closeDeleteTableModal: function () {
			this.isDeleteTableModalVisible = false;

			this.selectedTable = {
				id: null,
				name: null
			};
		},
		handleFileUpload: function () {
			this.file = this.$refs.file.files[0];
		},
		handleNewFileUpload: function () {
			this.newfile = this.$refs.newfile.files[0];
		},
		submitFile: function () {
			let vm = this;
			let formData = new FormData();
			formData.append('name', vm.selectedTable.name);
			formData.append('csv', vm.file);
			axios.post('/api/' + vm.$selectedSite.slug + '/large-data-tables',
					formData,
					{
						headers: {
							'Content-Type': 'multipart/form-data'
						}
					}
			).then(function () {
				vm.$toastr.success('New LargeDataTable created successfully.');
				window.location.reload();
			}).catch(function (e) {
				vm.errors.record(e.response.data);
				vm.$toastr.error('New LargeDataTable create failed.', 'Error');
			});
		},
		editName: function () {
			let vm = this;
			let formData = new FormData();
			formData.append('name', vm.selectedTable.name);
			formData.append('csv', vm.newfile);
			formData.append('_method', 'PUT');
			axios.post('/api/' + vm.$selectedSite.slug + '/large-data-tables/' + vm.selectedTable.id,
					formData,
					{
						headers: {
							'Content-Type': 'multipart/form-data'
						}
					}
			).then(function () {
				vm.$toastr.success('LargeDataTable updated successfully.');
				window.location.reload();
			}).catch(function (e) {
				vm.errors.record(e.response.data);
				vm.$toastr.error('LargeDataTable update failed.', 'Error');
			});
		},
		deleteTable: function (id) {
			let vm = this;

			axios.delete('/api/' + vm.$selectedSite.slug + '/large-data-tables/' + id
			).then(function () {
				vm.$toastr.success('LargeDataTable deleted successfully.');
				window.location.reload();
			}).catch(function () {
				vm.$toastr.error('LargeDataTable delete failed.', 'Error');
			});
		},
		downloadFile: function (path) {
			let filename = path.substring(path.lastIndexOf('/') + 1);
			filename = location.protocol + '//' + location.host + '/storage/csv/' + filename;
			window.open(filename, '_blank');
		}
	}
}
</script>
